import React from 'react';
import {Image, Pressable, StyleSheet, View} from 'react-native';
import {Carousel} from 'react-responsive-carousel';
import {observer} from 'mobx-react-lite';
import {Fade, Placeholder, PlaceholderMedia} from 'rn-placeholder';

import {useWebScreenDimensions} from '../hooks/useWebScreenDimensions';
import {AppRoute} from '../models/AppRoute';
import useAppNavigation from '../navigation/useAppNavigation';
import {useStores} from '../stores/use-stores';
import theme from '../style/theme';
import {isBmOrSnsOrDev} from '../utils/tenants';

import Text from './ui-controls/Text';

import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader

const HeroCarousel = observer(() => {
  const {promotionsStore} = useStores();
  const navigation = useAppNavigation();
  const {isWebDesktopScreen} = useWebScreenDimensions();

  const onPress = (item: any) =>
    navigation.navigate(AppRoute.PROMOTION, {
      promotion: item,
    });

  const getHeroImageUrl = (item: {
    heroImageWeb: {url: any};
    heroImage: {url: any};
  }) =>
    isWebDesktopScreen && item.heroImageWeb && item.heroImageWeb.url
      ? item.heroImageWeb.url
      : item.heroImage.url;

  const CarouselCardItem = ({item}: {item: any}) => {
    return (
      <Pressable onPress={() => onPress(item)} style={styles.touchable}>
        <View
          style={styles.floatingContainer}
          testID="carousel-card-item-floating-container">
          <View
            style={styles.floatingImageHolder}
            testID="carousel-card-item-floating-image-holder">
            <Image
              testID="carousel-card-item-floating-image"
              source={{uri: getHeroImageUrl(item)}}
              style={
                isWebDesktopScreen
                  ? styles.floatingImageWebDesktop
                  : styles.floatingImage
              }
            />
          </View>
          <View style={styles.floating} testID="carousel-card-item-floating">
            <Text
              testID="carousel-card-item-floating-header"
              style={styles.floatingHeader}
              adjustsFontSizeToFit={true}
              numberOfLines={1}>
              {item.bannerHeadline?.toUpperCase() ||
                item.headline.toUpperCase()}
            </Text>
          </View>
        </View>
      </Pressable>
    );
  };

  if (!promotionsStore.carouselData?.length) {
    return (
      <View
        style={[
          styles.touchable,
          styles.floatingContainer,
          styles.floatingImageHolder,
        ]}>
        <Placeholder Animation={Fade}>
          <PlaceholderMedia
            style={[
              styles.placeholderContent,
              isWebDesktopScreen
                ? styles.floatingImageWebDesktop
                : styles.floatingImage,
            ]}
          />
        </Placeholder>
      </View>
    );
  }

  return (
    <Carousel
      showThumbs={false}
      autoPlay
      infiniteLoop
      interval={7500}
      showStatus={false}
      showArrows
      transitionTime={800}
      showIndicators={false}
      autoFocus>
      {promotionsStore.carouselData?.map(item => (
        <CarouselCardItem item={item} key={item.id} />
      ))}
    </Carousel>
  );
});

export default HeroCarousel;

const styles = StyleSheet.create({
  touchable: {
    paddingHorizontal: 8,
  },
  floatingContainer: {
    borderRadius: 4,
    marginBottom: 36,
  },
  floatingImageHolder: {
    overflow: 'hidden',
  },
  floatingImage: {
    aspectRatio: 760 / (300 + 200),
  },
  floatingImageWebDesktop: {
    // To temporarily render all images at same ratio while we get them all
    // Currently, the sample image is 760x300 (38 : 15)
    // https://aspectratiocalculator.com/
    aspectRatio: 38 / 15,
  },
  placeholderContent: {
    width: '100%',
    height: 'auto',
    borderRadius: 0,
  },
  floating: {
    position: 'absolute',
    bottom: -24,
    marginRight: 16,
    height: 36,
    left: 8,
    backgroundColor: theme.colour.background.hero.header,
    paddingHorizontal: 8,
    borderRadius: 2,
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 5,
    },
    shadowOpacity: 0.29,
    shadowRadius: 4.65,
    elevation: 7,
  },
  floatingHeader: {
    color: theme.colour.text.hero.header,
    fontSize: 18,
    fontWeight: 'bold',
    paddingTop: 4,
  },
});
